import React, { useEffect, useState } from 'react';
import qs from 'qs';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Typography, Button, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import WarningIcon from '@mui/icons-material/Warning';
import { history } from 'common/history';
import NcsBanner from './NcsBanner';
import { ncsSsoLogin } from 'api/profile';
import { myFirebase } from '../../firebase/firebase';
import { verifyAuth } from 'actions/authAction';

const STATUS = {
  LOADING: 'loading',
  ACTIVE_SESSION: 'active_session',
  ERROR_401: 'error_401',
  ERROR_503: 'error_503',
};


const useStyles = makeStyles(theme => ({
  pageWrapper: {
    backgroundColor: '#ffffff',
    minHeight: '100vh',
  },
  loadingBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 80,
    [theme.breakpoints.down('md')]: {
      paddingTop: 48,
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  errorBody: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 80,
    paddingBottom: 40,
    [theme.breakpoints.down('md')]: {
      paddingTop: 40,
      paddingBottom: 24,
      paddingLeft: 16,
      paddingRight: 16,
    },
  },
  warningIcon: {
    fontSize: 80,
    color: '#ffc220',
    marginBottom: 24,
    [theme.breakpoints.down('md')]: {
      fontSize: 56,
      marginBottom: 16,
    },
  },
  button: {
    backgroundColor: '#ffc220',
    fontSize: 14,
    marginTop: 24,
    textTransform: 'none',
    fontWeight: 'bold',
    padding: '10px 32px',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      padding: '12px 16px',
    },
  },
  bannerText: {
    backgroundColor: '#f8d7da',
    padding: '12px 24px',
    width: '100%',
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 1.6,
    boxSizing: 'border-box',
    [theme.breakpoints.down('md')]: {
      padding: '12px 16px',
      fontSize: 13,
    },
  },
}));

const SsoRedirectHandler = () => {
  const classes = useStyles();
  const location = useLocation();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const isVerifying = useSelector(state => state.auth.isVerifying);
  const userEmail = useSelector(state => state.auth.user && state.auth.user.email);

  const [status, setStatus] = useState(STATUS.LOADING);

  useEffect(() => {
    // Wait for Firebase auth verification to complete before acting
    if (isVerifying) return;

    // Also guard against the race where Redux hasn't hydrated yet but Firebase
    // already has an active session. Check currentUser directly so we never
    // call signInWithCustomToken on top of an existing session.
    const firebaseCurrentUser = myFirebase.auth().currentUser;
    if (isAuthenticated || firebaseCurrentUser) {
      setStatus(STATUS.ACTIVE_SESSION);
      return;
    }

    const { token, clientId } = qs.parse(location.search, { ignoreQueryPrefix: true });

    if (!token || !clientId) {
      setStatus(STATUS.ERROR_401);
      return;
    }

    let mounted = true;

    const doSsoLogin = async () => {
      try {
        const response = await ncsSsoLogin({ token, clientId });
        const data = response && response.data ? response.data : response;
        const { isNewUser, firebaseCustomToken, ncsSsoUserId, userData } = data;

        if (!mounted) return;

        if (isNewUser) {
          history.push('/ncs-register', {
            isSsoFlow: true,
            firebaseToken: firebaseCustomToken,
            userData: {
              fullName: (userData && userData.fullName) || '',
              email: userData && userData.email,
              mobileNumber: userData && userData.mobileNumber,
              dateOfBirth: userData && userData.dateOfBirth,
              districtId: userData && userData.districtId,
              stateId: userData && userData.stateId,
              ncsSsoUserId,
            },
          });
        } else {
          try {
            await myFirebase.auth().signInWithCustomToken(firebaseCustomToken);
            // Re-run verifyAuth so Redux picks up the new Firebase session,
            // loads the user profile, and handles the redirect via afterLogin
            if (mounted) dispatch(verifyAuth(false));
          } catch (firebaseError) {
            if (mounted) setStatus(STATUS.ERROR_503);
          }
        }
      } catch (error) {
        if (!mounted) return;
        const statusCode = error && error.response && error.response.status;
        if (statusCode === 401) {
          setStatus(STATUS.ERROR_401);
        } else {
          setStatus(STATUS.ERROR_503);
        }
      }
    };

    doSsoLogin();

    return () => { mounted = false; };
  }, [isAuthenticated, isVerifying, location.search]);

  if (status === STATUS.ACTIVE_SESSION) {
    return (
      <div className={classes.pageWrapper}>
        <NcsBanner />
        <div className={classes.bannerText}>
          Hello, it looks like you are already logged-in at the Mentor To Go app with{' '}
          <em>{userEmail}</em>. Please continue using your existing account.
        </div>
        <Container maxWidth="sm" className={classes.loadingBody}>
          <Button
            variant="contained"
            className={classes.button}
            onClick={() => history.push('/mentorship')}
          >
            Go to Dashboard
          </Button>
        </Container>
      </div>
    );
  }

  if (status === STATUS.ERROR_401 || status === STATUS.ERROR_503) {
    return (
      <div className={classes.pageWrapper}>
        <NcsBanner />
        <Container maxWidth="sm" className={classes.errorBody}>
          <WarningIcon className={classes.warningIcon} />
          <Typography variant="h6" align="center" style={{ fontWeight: 'bold' }}>
            We couldn&apos;t verify your details from NCS.
          </Typography>
          <Typography align="center" style={{ marginTop: 8, color: '#555' }}>
            Please go back and try again.
          </Typography>
          <Button
            variant="contained"
            className={classes.button}
            onClick={() => history.go(-1)}
          >
            GO BACK
          </Button>
        </Container>
      </div>
    );
  }

  // Default: loading state
  return (
    <div className={classes.pageWrapper}>
      <NcsBanner />
      <Container maxWidth="sm" className={classes.loadingBody}>
        <Typography variant="h6" align="center" style={{ fontWeight: 'bold', marginBottom: 8 }}>
          Just a moment&hellip;
        </Typography>
        <Typography align="center" style={{ color: '#555', marginBottom: 32 }}>
          Verifying your details and setting up your account.
        </Typography>
        <CircularProgress style={{ color: '#ffc220' }} size={48} />
      </Container>
    </div>
  );
};

export default SsoRedirectHandler;
